"use client";

import { useState, useEffect } from "react";
import { Card, CardContent } from "@/shadcn/ui/card";
import { getCDNImageURL } from "@/data/images";
import SuggestedComediansCarousel from "@/components/feed/SuggestedComediansCarousel";
import SuggestedComedianCard from "@/components/feed/cards/SuggestedComedianCard";
import UpcomingShowsFeedUnit from "@/components/feed/UpcomingShowsFeedUnitV2";
import { ComedianCardSkeleton, ShowCardSkeleton } from "./EventPageSkeletons";

interface EventPageCarouselsProps {
  authenticated: boolean;
}

export default function EventPageCarousels({
  authenticated,
}: EventPageCarouselsProps) {
  const [suggestedComedians, setSuggestedComedians] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showsLoading, setShowsLoading] = useState(true);

  useEffect(() => {
    // Fetch suggested comedians from the feed API
    const fetchSuggestedComedians = async () => {
      try {
        const res = await fetch("/api/feed-items/v2?filter=hot");
        if (!res.ok) throw new Error("Failed to fetch feed");
        const data = await res.json();

        if (data?.suggestedComedians) {
          // Transform to the expected format with image URLs
          const comedians = data.suggestedComedians.map((comedian: any) => ({
            ...comedian,
            imageUrl: comedian.about_img
              ? getCDNImageURL(comedian.about_img)
              : null,
          }));
          setSuggestedComedians(comedians);
        }
      } catch (error) {
        console.error("Failed to fetch suggested comedians:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestedComedians();
  }, []);

  // Simulate shows loading completion after mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
      {/* Shows Nearby Carousel */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold font-sans mb-6">
            Shows Nearby
          </h2>
          {showsLoading ? (
            <div className="space-y-4">
              <ShowCardSkeleton />
              <ShowCardSkeleton />
              <ShowCardSkeleton />
            </div>
          ) : (
            <div className="animate-in fade-in-50 slide-in-from-bottom-2 duration-500">
              <UpcomingShowsFeedUnit />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suggested Comedians Carousel */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold font-sans mb-6">
            Comedians You Might Like
          </h2>
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <ComedianCardSkeleton />
              <ComedianCardSkeleton />
              <ComedianCardSkeleton />
              <ComedianCardSkeleton />
            </div>
          ) : suggestedComedians.length > 0 ? (
            <div className="animate-in fade-in-50 slide-in-from-bottom-2 duration-500">
              <SuggestedComediansCarousel
                title=""
                location="event_page"
                slides={suggestedComedians.map((comedian) => ({
                  id: comedian.display_name ?? "",
                  Component: (
                    <SuggestedComedianCard
                      isLoggedOutState={!authenticated}
                      comedian={comedian}
                    />
                  ),
                }))}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
